import { type } from "arktype";
/**
 * In-app (non-push) preferences, stored in `user_app.app_preferences_raw`. Push preferences live in
 * `notification_preferences_raw`, which is push-only by contract — that's why this is a separate column.
 *
 * Server-synced rather than device-local on purpose: these follow the user across devices and survive a
 * logout, which the mobile `s_localPref` store does not.
 *
 * A NULL column reads as {@link createDefaultAppPreferences} via {@link withAppPreferenceDefaults}, so no
 * backfill. Plain-object arktype schema (no `.or()` of objects), so it's safe under the mobile Hermes
 * stub; the normalizer is plain JS so it behaves identically on mobile, where arktype is a no-op.
 */
export const appPreferencesSchema = type({
    /** Whether the streak milestone celebration popups appear. Streaks keep counting either way. */
    streakMilestonePopups: "boolean",
    /** Which streak figure the card sorter shows beside the flame. */
    cardSorterStreakStat: "'dailyStreak' | 'percentDays' | 'monthly'",
});
export const CARD_SORTER_STREAK_STATS = [
    "dailyStreak",
    "percentDays",
    "monthly",
];
/** Fresh, unshared defaults (safe to mutate). */
export function createDefaultAppPreferences() {
    return { streakMilestonePopups: true, cardSorterStreakStat: "dailyStreak" };
}
function isRecord(x) {
    return typeof x === "object" && x !== null && !Array.isArray(x);
}
function isCardSorterStreakStat(x) {
    return typeof x === "string" && CARD_SORTER_STREAK_STATS.includes(x);
}
/**
 * Interpret a raw `app_preferences_raw` value (NULL / partial / malformed) as a complete
 * {@link AppPreferences}, defaulting per field. The read-time defaulter AND the server-side write
 * normalizer — an unknown stat or a non-boolean toggle falls back to the default rather than persisting.
 */
export function withAppPreferenceDefaults(raw) {
    const defaults = createDefaultAppPreferences();
    const r = isRecord(raw) ? raw : {};
    return {
        streakMilestonePopups: typeof r["streakMilestonePopups"] === "boolean" ? r["streakMilestonePopups"] : defaults.streakMilestonePopups,
        cardSorterStreakStat: isCardSorterStreakStat(r["cardSorterStreakStat"])
            ? r["cardSorterStreakStat"]
            : defaults.cardSorterStreakStat,
    };
}
//# sourceMappingURL=appPreferences.models.js.map